import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  PlusCircle,
  Save,
  Trash2,
  Shield,
  AlertTriangle,
  Pencil,
  CheckCircle2,
  XCircle,
  Crown,
  UserPlus,
  UserMinus,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";
import { useTranslation as useTranslationOrig } from "react-i18next";

// i18n safe
type RawT = (key: string, options?: Record<string, unknown>) => any;
const useTranslationSafe = (ns?: string) =>
  (useTranslationOrig as unknown as (ns?: any) => {
    t: RawT;
    i18n: { language: string };
  })(ns);

// —— Types ——
type Team = { id?: string; name: string; short_name?: string; group_name?: string };
type MatchRow = {
  id: string;
  team_home: string;
  team_away: string;
  start_time: string; // ISO
  court?: string | null;
  status: "scheduled" | "live" | "finished";
  score_home: number | null;
  score_away: number | null;
  table_de_marque?: string | null;
  arbitrage?: string | null;
};
type NewsRow = { id: string; title: string; content: string; created_at: string };
type AdminRow = { user_id: string; created_at: string };
type ProfileRow = { id: string; pseudo?: string | null; email?: string | null };

// —— Helpers ——
function toLocalInputValue(iso?: string) {
  if (!iso) return new Date().toISOString().slice(0, 16);
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60 * 1000);
  return local.toISOString().slice(0, 16);
}
function toISOFromLocalInput(localValue: string) {
  const d = new Date(localValue);
  return d.toISOString();
}
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const NEWS_BUCKET = "tournament-news";
const IMAGE_MARKDOWN_RE = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;

function sanitizeFileName(name: string) {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function renderNewsContent(content: string) {
  const parts: JSX.Element[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  while ((match = IMAGE_MARKDOWN_RE.exec(content)) !== null) {
    const [fullMatch, alt, src] = match;
    const start = match.index;

    if (start > lastIndex) {
      const textChunk = content.slice(lastIndex, start);
      if (textChunk.trim() || textChunk.includes("\n")) {
        parts.push(
          <div
            key={`text-${i}`}
            className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300"
          >
            {textChunk}
          </div>
        );
        i++;
      }
    }

    parts.push(
      <div key={`img-${i}`} className="my-3">
        <img
          src={src}
          alt={alt || "news image"}
          className="max-h-[420px] w-auto max-w-full rounded-lg border border-gray-200 dark:border-gray-800 object-contain"
          loading="lazy"
        />
        {alt ? (
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{alt}</div>
        ) : null}
      </div>
    );
    i++;

    lastIndex = start + fullMatch.length;
  }

  if (lastIndex < content.length) {
    const tail = content.slice(lastIndex);
    if (tail.trim() || tail.includes("\n")) {
      parts.push(
        <div
          key={`text-tail-${i}`}
          className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300"
        >
          {tail}
        </div>
      );
    }
  }

  if (parts.length === 0) {
    return (
      <div className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
        {content}
      </div>
    );
  }

  return <div className="space-y-2">{parts}</div>;
}

export default function TournamentAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const newsTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  // i18n
  const { t: tRaw, i18n } = useTranslationSafe("tournamentAdmin");
  const tr = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const out = tRaw(key, options);
      if (typeof out === "string") return out;
      if (Array.isArray(out)) return out.join("");
      return String(out ?? "");
    },
    [tRaw]
  );
  const lang = i18n?.language || "fr";
  const fmtDateTime = (iso: string) =>
    new Intl.DateTimeFormat(lang, { dateStyle: "short", timeStyle: "short" }).format(
      new Date(iso)
    );

const [canManage, setCanManage] = useState(false);
const [isOwner, setIsOwner] = useState(false);
  const [ownerId, setOwnerId] = useState<string | null>(null);

  // Teams
  const [teams, setTeams] = useState<Team[]>([]);
  const [newTeam, setNewTeam] = useState<Team>({ name: "", short_name: "", group_name: "" });

  // Matches
  const [matches, setMatches] = useState<MatchRow[]>([]);
  const [matchForm, setMatchForm] = useState<{
    team_home?: string;
    team_away?: string;
    start_time: string;
    court?: string;
    table_de_marque?: string;
    arbitrage?: string;
  }>({
    start_time: toLocalInputValue(),
    table_de_marque: "",
    arbitrage: "",
  });

  // News
  const [news, setNews] = useState<NewsRow[]>([]);
  const [newsForm, setNewsForm] = useState<{ title: string; content: string }>({
    title: "",
    content: "",
  });
  const [uploadingNewsImages, setUploadingNewsImages] = useState(false);

  // Admins
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [profiles, setProfiles] = useState<Record<string, ProfileRow>>({});
  const [adminInput, setAdminInput] = useState("");

  // Rules (règles du tournoi)
  const [rulesNote, setRulesNote] = useState<string>("");
  const [savingRules, setSavingRules] = useState(false);
  const [rulesSupported, setRulesSupported] = useState(true); // false si la colonne n'existe pas

  // — Load & guard —
useEffect(() => {
  (async () => {
    if (!id) return;

    let owner: string | null = null;
    try {
      const { data: t, error: tErr } = await supabase
        .from("tournaments")
        .select("user_id, rules_note")
        .eq("id", id)
        .maybeSingle();

      if (tErr) {
        setRulesSupported(false);
        const { data: t2 } = await supabase
          .from("tournaments")
          .select("user_id")
          .eq("id", id)
          .maybeSingle();
        owner = t2?.user_id ?? null;
      } else {
        owner = t?.user_id ?? null;
        setRulesSupported(true);
        setRulesNote(t?.rules_note ?? "");
      }
    } catch {
      setRulesSupported(false);
      const { data: t2 } = await supabase
        .from("tournaments")
        .select("user_id")
        .eq("id", id)
        .maybeSingle();
      owner = t2?.user_id ?? null;
    }

    const { data: auth } = await supabase.auth.getUser();
    const me = auth?.user?.id ?? null;

    setOwnerId(owner ?? null);

    const ownerOk = Boolean(me && owner && me === owner);
    setIsOwner(ownerOk);

    let adminOk = false;

    if (!ownerOk && me) {
      const { data: adminRow } = await supabase
        .from("tournament_admins")
        .select("user_id")
        .eq("tournament_id", id)
        .eq("user_id", me)
        .maybeSingle();

      adminOk = Boolean(adminRow);
    }

    const manageOk = ownerOk || adminOk;
    setCanManage(manageOk);

    if (!manageOk) {
      alert(
        tr("alerts.accessDenied", {
          defaultValue: "Accès refusé : vous n'êtes pas autorisé à gérer ce tournoi.",
        })
      );
      navigate(`/tournaments/${id}`);
      return;
    }

    const [{ data: te }, { data: mt }, { data: nw }, { data: ad }] = await Promise.all([
      supabase
        .from("tournament_teams")
        .select("id,name,short_name,group_name")
        .eq("tournament_id", id)
        .order("name"),
      supabase
        .from("tournament_matches")
        .select(
          "id,team_home,team_away,start_time,court,status,score_home,score_away,table_de_marque,arbitrage"
        )
        .eq("tournament_id", id)
        .order("start_time", { ascending: true }),
      supabase
        .from("tournament_news")
        .select("id,title,content,created_at")
        .eq("tournament_id", id)
        .order("created_at", { ascending: false }),
      supabase
        .from("tournament_admins")
        .select("user_id,created_at")
        .eq("tournament_id", id)
        .order("created_at", { ascending: true }),
    ]);

    setTeams(te || []);
    setMatches((mt as MatchRow[]) || []);
    setNews((nw as NewsRow[]) || []);
    setAdmins((ad as AdminRow[]) || []);

    const userIds = new Set<string>();
    if (owner) userIds.add(owner);
    (ad as AdminRow[] | null)?.forEach((a) => userIds.add(a.user_id));

    const ids = Array.from(userIds);
    if (ids.length) {
      const { data: pr, error: prErr } = await supabase
        .from("user_profiles")
        .select("id,pseudo")
        .in("id", ids);

      if (prErr) {
        console.error("profiles load error", prErr);
      }

      const map: Record<string, ProfileRow> = {};
      (pr || []).forEach((p) => {
        map[p.id] = p;
      });
      setProfiles(map);
    }
  })();
}, [id, navigate, tr]);

  // — Team actions —
  async function handleAddTeam(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !canManage) return;
    const payload = { ...newTeam, tournament_id: id } as any;
    const { error } = await supabase.from("tournament_teams").insert(payload);
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
    setNewTeam({ name: "", short_name: "", group_name: "" });
    const { data } = await supabase
      .from("tournament_teams")
      .select("id,name,short_name,group_name")
      .eq("tournament_id", id)
      .order("name");
    setTeams(data || []);
  }

  async function handleDeleteTeam(teamId: string) {
    if (!id || !canManage) return;
    if (!window.confirm(tr("teams.confirmDelete", { defaultValue: "Supprimer cette équipe ?" })))
      return;
    const { error } = await supabase.from("tournament_teams").delete().eq("id", teamId);
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
    setTeams((s) => s.filter((t) => t.id !== teamId));
  }

  // — Match actions —
  async function handleCreateMatch() {
    if (!id || !canManage || !matchForm.team_home || !matchForm.team_away) return;
    const { error } = await supabase.from("tournament_matches").insert({
      tournament_id: id,
      team_home: matchForm.team_home,
      team_away: matchForm.team_away,
      start_time: toISOFromLocalInput(matchForm.start_time),
      court: matchForm.court,
      status: "scheduled",
      table_de_marque: matchForm.table_de_marque?.trim() || null,
      arbitrage: matchForm.arbitrage?.trim() || null,
    });
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
    setMatchForm({ start_time: toLocalInputValue(), table_de_marque: "", arbitrage: "" });
    const { data } = await supabase
      .from("tournament_matches")
      .select(
        "id,team_home,team_away,start_time,court,status,score_home,score_away,table_de_marque,arbitrage"
      )
      .eq("tournament_id", id)
      .order("start_time", { ascending: true });
    setMatches((data as MatchRow[]) || []);
  }

  async function handleUpdateMatch(m: MatchRow) {
    if (!id || !canManage) return;
    const { error } = await supabase
      .from("tournament_matches")
      .update({
        start_time: m.start_time,
        court: m.court,
        status: m.status,
        score_home: m.score_home,
        score_away: m.score_away,
        table_de_marque: (m.table_de_marque ?? "").toString().trim() || null,
        arbitrage: (m.arbitrage ?? "").toString().trim() || null,
      })
      .eq("id", m.id);
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
  }

  async function handleDeleteMatch(matchId: string) {
    if (!isOwner) return;
    if (!window.confirm(tr("matches.confirmDelete", { defaultValue: "Supprimer ce match ?" })))
      return;
    const { error } = await supabase.from("tournament_matches").delete().eq("id", matchId);
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
    setMatches((s) => s.filter((m) => m.id !== matchId));
  }

  // — News images —
  function insertIntoNewsContent(textToInsert: string) {
    const textarea = newsTextareaRef.current;
    if (!textarea) {
      setNewsForm((s) => ({
        ...s,
        content: `${s.content}${s.content ? "\n" : ""}${textToInsert}`,
      }));
      return;
    }

    const start = textarea.selectionStart ?? newsForm.content.length;
    const end = textarea.selectionEnd ?? newsForm.content.length;

    setNewsForm((s) => {
      const before = s.content.slice(0, start);
      const after = s.content.slice(end);
      return {
        ...s,
        content: `${before}${textToInsert}${after}`,
      };
    });

    requestAnimationFrame(() => {
      const newPos = start + textToInsert.length;
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
    });
  }

  async function handleInsertNewsImages(files: FileList | null) {
    if (!files || !files.length || !id || !canManage) return;

    const imageFiles = Array.from(files).filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length === 0) {
      alert(tr("news.images.onlyImages", { defaultValue: "Merci de sélectionner uniquement des images." }));
      return;
    }

    setUploadingNewsImages(true);
    try {
      const { data: auth } = await supabase.auth.getUser();
      const userId = auth?.user?.id;
      if (!userId) {
        alert(tr("session.expired", { defaultValue: "Session expirée." }));
        return;
      }

      const markdownBlocks: string[] = [];

      for (const file of imageFiles) {
        const ext = file.name.split(".").pop() || "jpg";
        const safeName = sanitizeFileName(file.name.replace(/\.[^/.]+$/, "")) || "image";
        const path = `${id}/${userId}/${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}-${safeName}.${ext}`;

        const { error: uploadError } = await supabase.storage
          .from(NEWS_BUCKET)
          .upload(path, file, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          throw uploadError;
        }

        const { data: publicData } = supabase.storage.from(NEWS_BUCKET).getPublicUrl(path);
        const publicUrl = publicData?.publicUrl;

        if (!publicUrl) {
          throw new Error("Impossible de récupérer l’URL publique de l’image.");
        }

        markdownBlocks.push(`\n![${file.name}](${publicUrl})\n`);
      }

      insertIntoNewsContent(markdownBlocks.join("\n"));
    } catch (e: any) {
      alert(
        tr("errors.prefix", { defaultValue: "Erreur: " }) +
          (e?.message || "Impossible d’envoyer l’image.")
      );
    } finally {
      setUploadingNewsImages(false);
    }
  }

  // — News actions —
  async function handlePostNews(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !canManage) return;
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) return alert(tr("session.expired", { defaultValue: "Session expirée." }));

    const title = newsForm.title.trim();
    const content = newsForm.content.trim();

    if (!title || !content) {
      alert(tr("news.form.missing", { defaultValue: "Merci de renseigner un titre et un contenu." }));
      return;
    }

    const { error } = await supabase.from("tournament_news").insert({
      tournament_id: id,
      user_id: userId,
      title,
      content,
    });
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);

    setNewsForm({ title: "", content: "" });

    const { data } = await supabase
      .from("tournament_news")
      .select("id,title,content,created_at")
      .eq("tournament_id", id)
      .order("created_at", { ascending: false });
    setNews((data as NewsRow[]) || []);
  }

  async function handleDeleteNews(newsId: string) {
    if (!canManage) return;
    if (!window.confirm(tr("news.confirmDelete", { defaultValue: "Supprimer cette news ?" })))
      return;
    const { error } = await supabase.from("tournament_news").delete().eq("id", newsId);
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
    setNews((s) => s.filter((n) => n.id !== newsId));
  }

  // — Admins actions —
  async function refreshAdmins() {
    if (!id) return;
    const { data: ad } = await supabase
      .from("tournament_admins")
      .select("user_id,created_at")
      .eq("tournament_id", id)
      .order("created_at", { ascending: true });
    setAdmins((ad as AdminRow[]) || []);

    const ids = Array.from(
      new Set<string>([...(ad?.map((a) => a.user_id) || []), ownerId || ""])
    ).filter(Boolean);
    if (ids.length) {
      const { data: pr } = await supabase.from("user_profiles").select("id,pseudo").in("id", ids);
      const map: Record<string, ProfileRow> = {};
      (pr || []).forEach((p) => {
        map[p.id] = p;
      });
      setProfiles(map);
    }
  }

  async function handleAddAdmin(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !isOwner) return;
    const raw = adminInput.trim();
    if (!raw) return;

    let targetUserId: string | null = null;

    if (UUID_RE.test(raw)) {
      targetUserId = raw;
    } else {
      const { data: byPseudo } = await supabase
        .from("user_profiles")
        .select("id,pseudo")
        .eq("pseudo", raw)
        .maybeSingle();
      if (byPseudo?.id) {
        targetUserId = byPseudo.id;
      }
    }

    if (!targetUserId) {
      alert(tr("admins.searchNotFound", { defaultValue: "Utilisateur introuvable (pseudo exact)." }));
      return;
    }
    if (targetUserId === ownerId) {
      alert(tr("admins.ownerAlreadyAdmin", { defaultValue: "Le propriétaire est déjà admin par défaut." }));
      return;
    }

    const { error } = await supabase
      .from("tournament_admins")
      .upsert({ tournament_id: id, user_id: targetUserId }, { onConflict: "tournament_id,user_id" });
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);

    const { data: prOne } = await supabase
      .from("user_profiles")
      .select("id,pseudo")
      .eq("id", targetUserId)
      .maybeSingle();
    if (prOne) {
      setProfiles((s) => ({ ...s, [prOne.id]: prOne }));
    }
    setAdminInput("");
    await refreshAdmins();
  }

  async function handleRemoveAdmin(userId: string) {
    if (!id || !isOwner) return;
    if (!window.confirm(tr("admins.removeConfirm", { defaultValue: "Retirer cet admin ?" })))
      return;
    const { error } = await supabase
      .from("tournament_admins")
      .delete()
      .eq("tournament_id", id)
      .eq("user_id", userId);
    if (error) return alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
    await refreshAdmins();
  }

  // — Rules actions —
  async function handleSaveRules() {
    if (!id || !isOwner) return;
    setSavingRules(true);
    try {
      const { error } = await supabase.from("tournaments").update({ rules_note: rulesNote }).eq("id", id);
      if (error) {
        if (
          (error as any)?.message?.toLowerCase?.().includes("column") &&
          (error as any)?.message?.includes("rules_note")
        ) {
          setRulesSupported(false);
        }
        alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + error.message);
      }
    } catch (e: any) {
      alert(tr("errors.prefix", { defaultValue: "Erreur: " }) + (e?.message || "inconnue"));
    } finally {
      setSavingRules(false);
    }
  }

  const teamOptions = useMemo(() => teams.map((t) => ({ value: t.id!, label: t.name })), [teams]);
  const teamsById = useMemo(() => Object.fromEntries(teams.map((t) => [t.id!, t])), [teams]);

if (!canManage) {
  return (
    <div className="max-w-3xl mx-auto p-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
        <AlertTriangle className="w-5 h-5" /> {tr("restricted.title", { defaultValue: "Accès restreint" })}
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
        {tr("restricted.desc", {
          defaultValue: "Vous n’êtes pas autorisé à gérer ce tournoi.",
        })}
      </p>
    </div>
  );
}

  const inputBase =
    "border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500/40";

  const btnBase =
    "inline-flex items-center gap-2 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 hover:bg-gray-50 dark:hover:bg-gray-900 transition";

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6 text-gray-900 dark:text-gray-100">
      {/* Admins du tournoi */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
        <h2 className="font-bold mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" /> {tr("admins.title", { defaultValue: "Admins du tournoi" })}
        </h2>

        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className={`${inputBase} flex-1`}
            placeholder={tr("admins.inputPlaceholder", {
              defaultValue: "Email, pseudo ou UUID de l’utilisateur",
            })}
            value={adminInput}
            onChange={(e) => setAdminInput(e.target.value)}
          />
          <button onClick={(e) => handleAddAdmin(e as any)} className={btnBase}>
            <UserPlus className="w-4 h-4" /> {tr("common.add", { defaultValue: "Ajouter" })}
          </button>
        </div>

        <ul className="mt-3 space-y-2">
          {ownerId && (
            <li className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 bg-white dark:bg-gray-950 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Crown className="w-4 h-4 text-amber-500" />
                <div className="font-medium">{profiles[ownerId]?.pseudo || ownerId}</div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {tr("admins.ownerTag", { defaultValue: "(propriétaire)" })}
                </span>
              </div>
            </li>
          )}

          {admins
            .filter((a) => a.user_id !== ownerId)
            .map((a) => (
              <li
                key={a.user_id}
                className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 bg-white dark:bg-gray-950 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium">{profiles[a.user_id]?.pseudo || a.user_id}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {tr("admins.addedOn", { defaultValue: "Ajouté le" })} {fmtDateTime(a.created_at)}
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveAdmin(a.user_id)}
                  className="text-red-600 inline-flex items-center gap-1 text-sm hover:opacity-80"
                >
                  <UserMinus className="w-4 h-4" />
                  {tr("common.remove", { defaultValue: "Retirer" })}
                </button>
              </li>
            ))}

          {admins.filter((a) => a.user_id !== ownerId).length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {tr("admins.empty", { defaultValue: "Aucun admin additionnel pour l’instant." })}
            </div>
          )}
        </ul>
      </section>

      {/* ✅ Règles du tournoi */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
        <h2 className="font-bold mb-3 flex items-center gap-2">
          <Pencil className="w-4 h-4" /> {tr("rules.title", { defaultValue: "Règles du tournoi" })}
        </h2>

        {rulesSupported ? (
          <>
            <textarea
              className={`${inputBase} w-full min-h-[160px]`}
              placeholder={tr("rules.placeholder", {
                defaultValue:
                  "Renseigne ici les règles : format des matchs, fautes, timeouts, classements, consignes de table de marque et arbitrage, etc.",
              })}
              value={rulesNote}
              onChange={(e) => setRulesNote(e.target.value)}
            />
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>
                {tr("rules.hint", {
                  defaultValue:
                    "Astuce : tu peux écrire sur plusieurs lignes, ce sera affiché tel quel (retours à la ligne).",
                })}
              </span>
              <span>
                {rulesNote.length} {tr("rules.chars", { defaultValue: "caractères" })}
              </span>
            </div>
            <button onClick={handleSaveRules} disabled={savingRules} className={btnBase}>
              <Save className="w-4 h-4" />
              {savingRules
                ? tr("common.saving", { defaultValue: "Enregistrement…" })
                : tr("common.save", { defaultValue: "Enregistrer" })}
            </button>
          </>
        ) : (
          <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 p-3 text-sm">
            {tr("rules.notSupported.line1", {
              defaultValue: "La colonne rules_note n’existe pas encore dans tournaments.",
            })}
            <div className="mt-2">
              {tr("rules.notSupported.line2", {
                defaultValue: "Ajoute-la avec cette requête SQL (Supabase → SQL Editor) :",
              })}
            </div>
            <pre className="mt-2 p-2 bg-white dark:bg-gray-950 rounded border border-gray-200 dark:border-gray-800 overflow-auto">
{`ALTER TABLE public.tournaments
ADD COLUMN IF NOT EXISTS rules_note text;`}
            </pre>
            <div className="mt-2">
              {tr("rules.notSupported.line3", {
                defaultValue: "Recharge ensuite cette page pour éditer les règles.",
              })}
            </div>
          </div>
        )}
      </section>

      {/* Équipes */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
        <h2 className="font-bold mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4" /> {tr("teams.title", { defaultValue: "Gérer les équipes" })}
        </h2>
        <form className="grid sm:grid-cols-4 gap-2" onSubmit={handleAddTeam}>
          <input
            className={inputBase}
            placeholder={tr("teams.form.name", { defaultValue: "Nom équipe" })}
            value={newTeam.name}
            onChange={(e) => setNewTeam((s) => ({ ...s, name: e.target.value }))}
            required
          />
          <input
            className={inputBase}
            placeholder={tr("teams.form.short", { defaultValue: "Abrégé (ex: ABC)" })}
            value={newTeam.short_name || ""}
            onChange={(e) => setNewTeam((s) => ({ ...s, short_name: e.target.value }))}
          />
          <input
            className={inputBase}
            placeholder={tr("teams.form.group", { defaultValue: "Groupe (ex: A)" })}
            value={newTeam.group_name || ""}
            onChange={(e) => setNewTeam((s) => ({ ...s, group_name: e.target.value }))}
          />
          <button className={btnBase}>
            <PlusCircle className="w-4 h-4" />
            {tr("common.add", { defaultValue: "Ajouter" })}
          </button>
        </form>
        <ul className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {teams.map((t) => (
            <li
              key={t.id}
              className="border border-gray-200 dark:border-gray-800 rounded-lg p-2 bg-white dark:bg-gray-950 flex items-center justify-between"
            >
              <div>
                <div className="font-medium">{t.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t.short_name ? `${t.short_name} • ` : ""}
                  {t.group_name || "—"}
                </div>
              </div>
              <button
                onClick={() => t.id && handleDeleteTeam(t.id)}
                className="text-red-600 inline-flex items-center gap-1 text-sm hover:opacity-80"
              >
                <Trash2 className="w-4 h-4" />
                {tr("common.deleteShort", { defaultValue: "Suppr" })}
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Planifier un match */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
        <h2 className="font-bold mb-3 flex items-center gap-2">
          <Pencil className="w-4 h-4" /> {tr("matches.plan.title", { defaultValue: "Planifier un match" })}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <select
            className={inputBase}
            value={matchForm.team_home || ""}
            onChange={(e) => setMatchForm((s) => ({ ...s, team_home: e.target.value }))}
          >
            <option value="">{tr("matches.plan.homePlaceholder", { defaultValue: "Domicile…" })}</option>
            {teamOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className={inputBase}
            value={matchForm.team_away || ""}
            onChange={(e) => setMatchForm((s) => ({ ...s, team_away: e.target.value }))}
          >
            <option value="">{tr("matches.plan.awayPlaceholder", { defaultValue: "Extérieur…" })}</option>
            {teamOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            className={inputBase}
            value={matchForm.start_time}
            onChange={(e) => setMatchForm((s) => ({ ...s, start_time: e.target.value }))}
          />
          <input
            className={inputBase}
            placeholder={tr("matches.plan.courtPlaceholder", { defaultValue: "Terrain (ex: T1)" })}
            value={matchForm.court || ""}
            onChange={(e) => setMatchForm((s) => ({ ...s, court: e.target.value }))}
          />
          <input
            className={`${inputBase} lg:col-span-2`}
            placeholder={tr("matches.plan.tablePlaceholder", {
              defaultValue: "Table de marque (noms, consignes, etc.)",
            })}
            value={matchForm.table_de_marque || ""}
            onChange={(e) => setMatchForm((s) => ({ ...s, table_de_marque: e.target.value }))}
          />
          <input
            className={`${inputBase} lg:col-span-2`}
            placeholder={tr("matches.plan.refPlaceholder", {
              defaultValue: "Arbitrage (noms des arbitres, rôles, etc.)",
            })}
            value={matchForm.arbitrage || ""}
            onChange={(e) => setMatchForm((s) => ({ ...s, arbitrage: e.target.value }))}
          />
        </div>
        <button onClick={handleCreateMatch} className={`${btnBase} mt-3`}>
          <Save className="w-4 h-4" />
          {tr("common.save", { defaultValue: "Enregistrer" })}
        </button>
      </section>

      {/* Matches */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
        <h2 className="font-bold mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" /> {tr("matches.list.title", { defaultValue: "Matches" })}
        </h2>
        {matches.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {tr("matches.list.empty", { defaultValue: "Aucun match planifié." })}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
            <table className="min-w-full text-sm">
              <thead className="text-left bg-gray-50 dark:bg-gray-900">
                <tr className="border-b border-gray-200 dark:border-gray-800">
                  <th className="py-2 pr-3">{tr("matches.list.th.datetime", { defaultValue: "Date/Heure" })}</th>
                  <th className="py-2 pr-3">{tr("matches.list.th.match", { defaultValue: "Match" })}</th>
                  <th className="py-2 pr-3">{tr("matches.list.th.court", { defaultValue: "Terrain" })}</th>
                  <th className="py-2 pr-3">{tr("matches.list.th.table", { defaultValue: "Table de marque" })}</th>
                  <th className="py-2 pr-3">{tr("matches.list.th.referees", { defaultValue: "Arbitrage" })}</th>
                  <th className="py-2 pr-3">{tr("matches.list.th.status", { defaultValue: "Statut" })}</th>
                  <th className="py-2 pr-3">{tr("matches.list.th.score", { defaultValue: "Score" })}</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-950">
                {matches.map((m) => (
                  <tr key={m.id} className="border-b last:border-0 border-gray-200 dark:border-gray-800">
                    <td className="py-2 pr-3 whitespace-nowrap">
                      <input
                        type="datetime-local"
                        className={`${inputBase} px-2 py-1`}
                        value={toLocalInputValue(m.start_time)}
                        onChange={(e) => {
                          const iso = toISOFromLocalInput(e.target.value);
                          setMatches((s) => s.map((x) => (x.id === m.id ? { ...x, start_time: iso } : x)));
                        }}
                        onBlur={() => handleUpdateMatch(m)}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      {teamsById[m.team_home]?.name || "?"} {tr("common.vs", { defaultValue: "vs" })}{" "}
                      {teamsById[m.team_away]?.name || "?"}
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        className={`${inputBase} px-2 py-1 w-24`}
                        value={m.court || ""}
                        onChange={(e) =>
                          setMatches((s) => s.map((x) => (x.id === m.id ? { ...x, court: e.target.value } : x)))
                        }
                        onBlur={() => handleUpdateMatch(m)}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        className={`${inputBase} px-2 py-1 w-56`}
                        placeholder={tr("matches.list.tablePlaceholder", { defaultValue: "Table de marque" })}
                        value={m.table_de_marque || ""}
                        onChange={(e) =>
                          setMatches((s) =>
                            s.map((x) => (x.id === m.id ? { ...x, table_de_marque: e.target.value } : x))
                          )
                        }
                        onBlur={() => handleUpdateMatch(m)}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <input
                        className={`${inputBase} px-2 py-1 w-56`}
                        placeholder={tr("matches.list.refPlaceholder", { defaultValue: "Arbitrage" })}
                        value={m.arbitrage || ""}
                        onChange={(e) =>
                          setMatches((s) =>
                            s.map((x) => (x.id === m.id ? { ...x, arbitrage: e.target.value } : x))
                          )
                        }
                        onBlur={() => handleUpdateMatch(m)}
                      />
                    </td>
                    <td className="py-2 pr-3">
                      <select
                        className={`${inputBase} px-2 py-1`}
                        value={m.status}
                        onChange={(e) =>
                          setMatches((s) =>
                            s.map((x) => (x.id === m.id ? { ...x, status: e.target.value as MatchRow["status"] } : x))
                          )
                        }
                        onBlur={() => handleUpdateMatch(m)}
                      >
                        <option value="scheduled">{tr("status.scheduled", { defaultValue: "programmé" })}</option>
                        <option value="live">{tr("status.live", { defaultValue: "live" })}</option>
                        <option value="finished">{tr("status.finished", { defaultValue: "terminé" })}</option>
                      </select>
                    </td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          className={`${inputBase} px-2 py-1 w-16`}
                          placeholder={tr("matches.score.home", { defaultValue: "Domicile" })}
                          value={m.score_home ?? ""}
                          onChange={(e) =>
                            setMatches((s) =>
                              s.map((x) =>
                                x.id === m.id
                                  ? { ...x, score_home: e.target.value === "" ? null : Number(e.target.value) }
                                  : x
                              )
                            )
                          }
                          onBlur={() => handleUpdateMatch(m)}
                        />
                        <span className="text-gray-500 dark:text-gray-400">-</span>
                        <input
                          type="number"
                          className={`${inputBase} px-2 py-1 w-16`}
                          placeholder={tr("matches.score.away", { defaultValue: "Extérieur" })}
                          value={m.score_away ?? ""}
                          onChange={(e) =>
                            setMatches((s) =>
                              s.map((x) =>
                                x.id === m.id
                                  ? { ...x, score_away: e.target.value === "" ? null : Number(e.target.value) }
                                  : x
                              )
                            )
                          }
                          onBlur={() => handleUpdateMatch(m)}
                        />
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-right">
                      <button
                        onClick={() => handleDeleteMatch(m.id)}
                        className="text-red-600 inline-flex items-center gap-1 text-sm hover:opacity-80"
                      >
                        <Trash2 className="w-4 h-4" />
                        {tr("common.deleteShort", { defaultValue: "Suppr" })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* News */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-950">
        <h2 className="font-bold mb-3 flex items-center gap-2">
          <XCircle className="w-4 h-4" /> {tr("news.title", { defaultValue: "News du tournoi" })}
        </h2>

        <form className="space-y-2" onSubmit={handlePostNews}>
          <input
            className={`${inputBase} w-full`}
            placeholder={tr("news.form.titlePlaceholder", { defaultValue: "Titre" })}
            required
            value={newsForm.title}
            onChange={(e) => setNewsForm((s) => ({ ...s, title: e.target.value }))}
          />

          <textarea
            ref={newsTextareaRef}
            className={`${inputBase} w-full`}
            rows={8}
            placeholder={tr("news.form.contentPlaceholder", {
              defaultValue: "Contenu…",
            })}
            required
            value={newsForm.content}
            onChange={(e) => setNewsForm((s) => ({ ...s, content: e.target.value }))}
          />

          <div className="flex flex-wrap items-center gap-2">
            <label className={`${btnBase} cursor-pointer`}>
              {uploadingNewsImages ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ImageIcon className="w-4 h-4" />
              )}
              {uploadingNewsImages
                ? tr("news.images.uploading", { defaultValue: "Envoi des images…" })
                : tr("news.images.insert", { defaultValue: "Insérer une image" })}
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                disabled={uploadingNewsImages}
                onChange={(e) => {
                  handleInsertNewsImages(e.target.files);
                  e.target.value = "";
                }}
              />
            </label>

            <button className={btnBase} type="submit" disabled={uploadingNewsImages}>
              <PlusCircle className="w-4 h-4" />
              {tr("news.form.publish", { defaultValue: "Publier" })}
            </button>
          </div>

          <div className="text-xs text-gray-500 dark:text-gray-400">
            {tr("news.images.hint", {
              defaultValue:
                "Les images sélectionnées seront insérées à l’endroit du curseur dans le corps de la news.",
            })}
          </div>
        </form>

        <ul className="mt-4 space-y-2">
          {news.map((n) => (
            <li
              key={n.id}
              className="rounded-lg border border-gray-200 dark:border-gray-800 p-3 bg-white dark:bg-gray-950 flex items-start justify-between gap-3"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs text-gray-500 dark:text-gray-400">{fmtDateTime(n.created_at)}</div>
                <div className="font-semibold">{n.title}</div>
                <div className="mt-1">{renderNewsContent(n.content)}</div>
              </div>
              <button
                onClick={() => handleDeleteNews(n.id)}
                className="text-red-600 inline-flex items-center gap-1 text-sm hover:opacity-80 shrink-0"
              >
                <Trash2 className="w-4 h-4" />
                {tr("common.deleteShort", { defaultValue: "Suppr" })}
              </button>
            </li>
          ))}

          {news.length === 0 && (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {tr("news.empty", { defaultValue: "Aucune news pour l’instant." })}
            </div>
          )}
        </ul>
      </section>
    </div>
  );
}